import { Application, Sprite, Graphics } from 'pixi.js';
import { AssetLoader } from './game/AssetLoader';
import { Player } from './game/Player';
import { InputManager } from './game/InputManager';
import { ObstacleManager } from './game/ObstacleManager';
import { PlatformManager } from './game/PlatformManager';
import { GameOverScreen } from './game/GameOverScreen';
import { ScoreManager } from './game/ScoreManager';
import { UI } from './game/UI';
import { StartScreen } from './game/StartScreen';
import { SnowEffect } from './game/SnowEffect';
import { DifficultyManager } from './game/DifficultyManager';

class Game {
  private app: Application;
  private gameWidth: number;
  private gameHeight: number;
  private assetLoader: AssetLoader;
  private background?: Sprite;
  private player?: Player;
  private inputManager?: InputManager;
  private obstacleManager?: ObstacleManager;
  private platformManager?: PlatformManager;
  private gameOverScreen?: GameOverScreen;
  private scoreManager?: ScoreManager;
  private difficultyManager?: DifficultyManager;
  private ui?: UI;
  private startScreen?: StartScreen;
  private snowEffect?: SnowEffect;
  private selectedCat: 'alici' | 'jasper' = 'alici';
  private isGameOver = false;
  private isGameStarted = false;
  private isWalkInComplete = false;
  private debugMode = false;
  private debugGraphics?: Graphics;

  constructor() {
    this.gameWidth = window.innerWidth;
    this.gameHeight = window.innerHeight;
    this.app = new Application();
    this.assetLoader = AssetLoader.getInstance();
    this.init();
  }

  private async init(): Promise<void> {
    await this.app.init({
      width: this.gameWidth,
      height: this.gameHeight,
      backgroundColor: 0x000000,
      resolution: window.devicePixelRatio || 1,
      autoDensity: true,
      antialias: true,
    });

    const container = document.getElementById('game-container');
    if (container) {
      container.appendChild(this.app.canvas);
    }

    this.lockOrientation();
    this.setupResize();
    this.resize();

    await this.loadAssets();
    await this.loadFonts();
    this.setupScene();
    this.setupInput();
    this.setupDebugMode();

    this.app.ticker.add(this.gameLoop.bind(this));

    console.log('Game initialized');
  }

  private async loadAssets(): Promise<void> {
    await this.assetLoader.loadAssets();
  }

  private async loadFonts(): Promise<void> {
    // Wait for Rubik font to load
    if ('fonts' in document) {
      try {
        await document.fonts.load('400 24px Rubik');
        await document.fonts.load('700 24px Rubik');
        console.log('Rubik font loaded');
      } catch (error) {
        console.warn('Failed to load Rubik font:', error);
      }
    }
  }

  private setupScene(): void {
    this.createBackground();
    this.createPlayer();
    this.createObstacleManager();
    this.createPlatformManager();
    this.createScoreManager();
    this.createDifficultyManager();
    this.createUI();
    this.createGameOverScreen();
    this.createStartScreen();
    this.createSnowEffect();
  }

  private createBackground(): void {
    const texture = this.assetLoader.getTexture('background');
    this.background = new Sprite(texture);

    this.background.width = this.gameWidth;
    this.background.height = this.gameHeight;
    this.background.x = 0;
    this.background.y = 0;

    this.app.stage.addChild(this.background);
  }

  private createPlayer(): void {
    this.player = new Player(this.gameWidth, this.gameHeight, this.selectedCat);
    this.player.visible = false; // Hide player until game starts
    this.app.stage.addChild(this.player);
  }

  private createObstacleManager(): void {
    if (!this.player) return;

    this.obstacleManager = new ObstacleManager(this.gameWidth, this.player.getGroundY());
    this.app.stage.addChild(this.obstacleManager);
  }

  private createPlatformManager(): void {
    if (!this.player) return;

    this.platformManager = new PlatformManager(this.gameWidth, this.player.getGroundY());
    this.platformManager.onTreatCollectedCallback(points => {
      if (this.scoreManager) {
        this.scoreManager.addBonusPoints(points);
      }
    });
    this.app.stage.addChild(this.platformManager);
  }

  private createScoreManager(): void {
    this.scoreManager = new ScoreManager();
  }

  private createDifficultyManager(): void {
    this.difficultyManager = new DifficultyManager();
  }

  private createUI(): void {
    this.ui = new UI(this.gameWidth, this.gameHeight);
    this.ui.visible = false; // Hide UI on start screen
    this.ui.onScoreClick(() => {
      this.debugMode = !this.debugMode;
      console.log('Debug mode:', this.debugMode ? 'ON' : 'OFF');
    });
    this.app.stage.addChild(this.ui);
  }

  private createGameOverScreen(): void {
    this.gameOverScreen = new GameOverScreen(this.gameWidth, this.gameHeight);
    this.gameOverScreen.onClick(() => this.returnToStart());
    this.app.stage.addChild(this.gameOverScreen);
  }

  private createStartScreen(): void {
    this.startScreen = new StartScreen(this.gameWidth, this.gameHeight, this.scoreManager!);
    this.startScreen.onCatSelected(cat => this.startGame(cat));
    this.app.stage.addChild(this.startScreen);
  }

  private createSnowEffect(): void {
    const groundY = this.player ? this.player.getGroundY() : this.gameHeight * 0.92;
    this.snowEffect = new SnowEffect(this.gameWidth, this.gameHeight, groundY);
    this.app.stage.addChild(this.snowEffect);
  }

  private lockOrientation(): void {
    // Try to lock orientation using Screen Orientation API
    if (screen.orientation && 'lock' in screen.orientation) {
      (screen.orientation as any).lock('landscape-primary').catch((error: Error) => {
        console.log('Screen orientation lock not supported or denied:', error.message);
      });
    }

    // Monitor orientation changes for devices that don't support locking
    const handleOrientationChange = () => {
      const isPortrait =
        window.innerHeight > window.innerWidth || (screen.orientation && screen.orientation.type.includes('portrait'));

      if (isPortrait) {
        console.warn('Portrait mode detected - game should be played in landscape');
        // The CSS will handle hiding the game and showing rotation warning
      } else {
        console.log('Landscape mode active');
      }
    };

    // Listen to both orientation change events for maximum compatibility
    window.addEventListener('orientationchange', handleOrientationChange);
    screen.orientation?.addEventListener('change', handleOrientationChange);

    // Check initial orientation
    handleOrientationChange();

    // iOS-specific: Request fullscreen on user interaction
    const requestFullscreen = () => {
      const elem = document.documentElement as any;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen().catch(() => {});
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen().catch(() => {});
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen().catch(() => {});
      }
    };

    // Attempt fullscreen on first user interaction (helps with iOS)
    const enableFullscreen = () => {
      requestFullscreen();
      document.removeEventListener('touchstart', enableFullscreen);
      document.removeEventListener('click', enableFullscreen);
    };

    document.addEventListener('touchstart', enableFullscreen, { once: true });
    document.addEventListener('click', enableFullscreen, { once: true });
  }

  private setupInput(): void {
    this.inputManager = new InputManager(this.app.canvas);

    this.inputManager.onJumpStart(() => {
      if (this.player && this.isGameStarted && !this.isGameOver) {
        this.player.startJump();
      }
    });

    this.inputManager.onJumpRelease(() => {
      if (this.player && this.isGameStarted && !this.isGameOver) {
        this.player.stopJump();
      }
    });
  }

  private setupDebugMode(): void {
    window.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'd') {
        this.debugMode = !this.debugMode;
        console.log('Debug mode:', this.debugMode ? 'ON' : 'OFF');
      }
    });

    this.debugGraphics = new Graphics();
    this.app.stage.addChild(this.debugGraphics);
  }

  private setupResize(): void {
    window.addEventListener('resize', () => this.resize());
    screen.orientation?.addEventListener('change', () => this.resize());
  }

  private resize(): void {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    this.gameWidth = windowWidth;
    this.gameHeight = windowHeight;

    this.app.renderer.resize(windowWidth, windowHeight);

    if (this.background) {
      this.background.width = this.gameWidth;
      this.background.height = this.gameHeight;
    }

    if (this.player) {
      this.player.updatePosition(this.gameWidth, this.gameHeight);
    }

    if (this.obstacleManager && this.player) {
      this.obstacleManager.updateDimensions(this.gameWidth, this.player.getGroundY());
    }

    if (this.platformManager && this.player) {
      this.platformManager.updateDimensions(this.gameWidth, this.player.getGroundY());
    }

    if (this.ui) {
      this.ui.updateDimensions(this.gameWidth, this.gameHeight);
    }

    if (this.gameOverScreen) {
      this.gameOverScreen.updateDimensions(this.gameWidth, this.gameHeight);
    }

    if (this.startScreen) {
      this.startScreen.updateDimensions(this.gameWidth, this.gameHeight);
    }

    if (this.snowEffect && this.player) {
      this.snowEffect.updateDimensions(this.gameWidth, this.gameHeight, this.player.getGroundY());
    }
  }

  private gameLoop(ticker: any): void {
    const deltaTime = ticker.deltaMS / 1000;

    if (this.snowEffect) {
      this.snowEffect.update(deltaTime);
    }

    if (this.startScreen) {
      this.startScreen.update(deltaTime);
    }

    if (this.isGameOver || !this.isGameStarted) {
      return;
    }

    if (this.player) {
      this.player.update(deltaTime);

      // Check if walk-in animation just completed
      if (!this.isWalkInComplete && this.player.isWalkingInComplete()) {
        this.isWalkInComplete = true;

        // Start game systems after walk-in
        if (this.scoreManager) {
          this.scoreManager.start();
        }
        if (this.difficultyManager) {
          this.difficultyManager.start();
        }
      }
    }

    // Don't update game systems until walk-in is complete
    if (!this.isWalkInComplete) {
      return;
    }

    if (this.scoreManager) {
      this.scoreManager.update(deltaTime);
    }

    if (this.difficultyManager) {
      this.difficultyManager.update(deltaTime);

      if (this.obstacleManager) {
        this.obstacleManager.setScrollSpeed(this.difficultyManager.getCurrentSpeed());
      }

      if (this.platformManager) {
        this.platformManager.setScrollSpeed(this.difficultyManager.getCurrentSpeed());
      }
    }

    if (this.ui && this.scoreManager) {
      this.ui.updateScore(this.scoreManager.getCurrentScore());
    }

    if (this.ui && this.difficultyManager && this.obstacleManager) {
      this.ui.updateDebugInfo(
        this.difficultyManager.getCurrentSpeed(),
        this.obstacleManager.getCurrentSpawnInterval(),
        this.debugMode
      );
    }

    if (this.obstacleManager) {
      this.obstacleManager.update(deltaTime);

      if (this.player) {
        const playerHitbox = this.player.getHitbox();
        const collision = this.obstacleManager.checkCollision(playerHitbox, this.player.getIsStunned());

        if (collision.type === 'solid') {
          this.gameOver();
        } else if (collision.type === 'poop') {
          this.player.triggerStun();
        }
      }
    }

    if (this.platformManager && this.player) {
      this.platformManager.update(deltaTime);

      const playerHitbox = this.player.getHitbox();
      const playerX = this.player.x;
      const playerY = this.player.y;
      const velocityY = this.player.getVelocityY();

      // Check platform collisions
      const platformCollision = this.platformManager.checkPlatformCollisions(playerHitbox, velocityY);

      if (
        platformCollision.type === 'land' &&
        platformCollision.platform &&
        !this.player.getIsBonkSticking() &&
        !this.player.hasPostBonkCooldown()
      ) {
        this.player.landOnPlatform(platformCollision.platform, platformCollision.platform.getPlatformY());
      } else if (platformCollision.type === 'bonk' && platformCollision.platform) {
        this.player.bonkPlatform();
      }

      // Check treat collisions
      this.platformManager.checkTreatCollisions(playerX, playerY, playerHitbox);
    }

    this.drawDebugHitboxes();
  }

  private drawDebugHitboxes(): void {
    if (!this.debugMode || !this.debugGraphics) {
      this.debugGraphics?.clear();
      return;
    }

    this.debugGraphics.clear();

    if (this.player) {
      const playerHitbox = this.player.getHitbox();
      this.debugGraphics.rect(playerHitbox.x, playerHitbox.y, playerHitbox.width, playerHitbox.height);
      this.debugGraphics.stroke({ width: 2, color: 0x00ff00 });
    }

    if (this.obstacleManager) {
      for (const child of this.obstacleManager.children) {
        const obstacle = child as any;
        if (obstacle.getHitbox) {
          const hitbox = obstacle.getHitbox();
          this.debugGraphics.rect(hitbox.x, hitbox.y, hitbox.width, hitbox.height);
          this.debugGraphics.stroke({ width: 2, color: 0xff0000 });
        }
      }
    }

    if (this.platformManager) {
      for (const platform of this.platformManager.getPlatforms()) {
        const topHitbox = platform.getTopHitbox();
        this.debugGraphics.rect(topHitbox.x, topHitbox.y, topHitbox.width, topHitbox.height);
        this.debugGraphics.stroke({ width: 2, color: 0x0000ff });

        const bottomHitbox = platform.getBottomHitbox();
        this.debugGraphics.rect(bottomHitbox.x, bottomHitbox.y, bottomHitbox.width, bottomHitbox.height);
        this.debugGraphics.stroke({ width: 2, color: 0xffff00 });
      }
    }
  }

  private gameOver(): void {
    this.isGameOver = true;

    if (this.player) {
      this.player.setGameOver();
    }

    if (this.scoreManager) {
      this.scoreManager.stop();
    }

    if (this.difficultyManager) {
      this.difficultyManager.stop();
    }

    if (this.gameOverScreen && this.scoreManager) {
      this.gameOverScreen.show(
        this.scoreManager.getCurrentScore(),
        this.scoreManager.getFormattedTime(),
        this.scoreManager.getBestScore()
      );
    }
  }

  private startGame(cat: 'alici' | 'jasper'): void {
    this.selectedCat = cat;
    this.isGameStarted = true;
    this.isWalkInComplete = false;

    if (this.startScreen) {
      this.startScreen.hide();
    }

    if (this.player) {
      this.player.setCat(cat);
      this.player.visible = true;
      this.player.startWalkIn(this.gameWidth);
    }

    if (this.ui) {
      this.ui.visible = true;
    }

    if (this.obstacleManager) {
      const rivalCat = cat === 'alici' ? 'jasper' : 'alici';
      this.obstacleManager.setRivalCat(rivalCat);
    }

    if (this.scoreManager) {
      this.scoreManager.setCat(cat);
      // Don't start scoring until walk-in is complete
    }

    if (this.difficultyManager) {
      // Don't start difficulty until walk-in is complete
    }
  }

  private returnToStart(): void {
    this.isGameOver = false;
    this.isGameStarted = false;
    this.isWalkInComplete = false;

    if (this.gameOverScreen) {
      this.gameOverScreen.hide();
    }

    if (this.startScreen) {
      this.startScreen.show();
    }

    if (this.player) {
      this.player.visible = false; // Hide player on start screen
    }

    if (this.ui) {
      this.ui.visible = false; // Hide UI on start screen
    }

    if (this.scoreManager) {
      this.scoreManager.reset();
    }

    if (this.difficultyManager) {
      this.difficultyManager.reset();
    }

    if (this.obstacleManager) {
      this.obstacleManager.reset();
    }

    if (this.platformManager) {
      this.platformManager.reset();
    }

    if (this.player) {
      this.player.updatePosition(this.gameWidth, this.gameHeight);
    }
  }
}

new Game();
